import { fetchJson, IDP_TIMEOUT_MS } from './http';
import { IdpAuthError } from './provider';

/**
 * Discovery OIDC: o app so conhece o issuer, todo endpoint vem do
 * `.well-known/openid-configuration`.
 *
 * Endpoint montado na mao vira drift silencioso no dia em que o realm mudar de
 * forma — o contrato manda hardcodar o issuer e mais nada
 * (`seazone-idp/docs/integracao/fluxo-oidc.md`, passo 0).
 */

const UMA_HORA_MS = 3_600_000;

export interface IdpDiscovery {
  readonly issuer: string;
  readonly authorizationEndpoint: string;
  readonly tokenEndpoint: string;
  readonly jwksUri: string;
  readonly endSessionEndpoint?: string;
}

interface DiscoveryDocument {
  readonly issuer?: string;
  readonly authorization_endpoint?: string;
  readonly token_endpoint?: string;
  readonly jwks_uri?: string;
  readonly end_session_endpoint?: string;
}

export interface DiscoveryOptions {
  readonly issuer: string;
  readonly fetchImpl?: typeof fetch;
  readonly timeoutMs?: number;
  readonly ttlMs?: number;
  readonly now?: () => number;
}

function parse(issuer: string, document: DiscoveryDocument): IdpDiscovery {
  // Byte a byte: issuer que nao bate e realm errado (stg no lugar de prd), e
  // e a unica checagem que pega isso antes de o usuario logar no lugar errado.
  if (document.issuer !== issuer) {
    throw new IdpAuthError(
      'discovery_issuer_divergente',
      `Discovery devolveu issuer ${String(document.issuer)}, esperado ${issuer}.`,
    );
  }
  const { authorization_endpoint: authorize, token_endpoint: token, jwks_uri: jwks } = document;
  if (authorize === undefined || token === undefined || jwks === undefined) {
    throw new IdpAuthError('discovery_incompleto', 'Discovery do IDP veio sem endpoint essencial.');
  }
  return {
    issuer,
    authorizationEndpoint: authorize,
    tokenEndpoint: token,
    jwksUri: jwks,
    ...(document.end_session_endpoint !== undefined && {
      endSessionEndpoint: document.end_session_endpoint,
    }),
  };
}

/**
 * Devolve uma funcao que resolve o discovery com cache por TTL.
 *
 * A promise fica no cache, e nao o valor resolvido: assim dois logins
 * simultaneos no arranque dividem um discovery so. Falha limpa o cache na hora
 * — IDP fora do ar por um minuto nao pode virar cache negativo permanente.
 */
export function createDiscovery(options: DiscoveryOptions): () => Promise<IdpDiscovery> {
  const {
    issuer,
    fetchImpl,
    timeoutMs = IDP_TIMEOUT_MS,
    ttlMs = UMA_HORA_MS,
    now = () => Date.now(),
  } = options;
  const url = `${issuer.replace(/\/+$/, '')}/.well-known/openid-configuration`;

  let cache: { readonly at: number; readonly value: Promise<IdpDiscovery> } | undefined;

  return () => {
    if (cache !== undefined && now() - cache.at < ttlMs) return cache.value;

    const pending = fetchJson<DiscoveryDocument>(url, {
      timeoutMs,
      ...(fetchImpl && { fetchImpl }),
    })
      .then((document) => parse(issuer, document))
      .catch((error: unknown) => {
        cache = undefined;
        throw error;
      });

    cache = { at: now(), value: pending };
    return pending;
  };
}
